import { computed, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RsOverviewServerSideStateManager } from '@rs-shared-lib/abstract-classes/rs-overview-state/rs-overview-server-side-state-manager';
import { RsPersistOverviewState } from '@rs-shared-lib/abstract-classes/rs-overview-state/persistency/rs-persist-overview-state.decorator';
import { rsCountFilters } from '@rs-shared-lib/utils/count-filters/count-filters';
import { UserService } from '@core/services/user.service';
import { DoubleMaintenanceSummary } from '@app/leasing-company/maintenance-intervals/double-maintenance/shared/models/double-maintenance-summary';
import { MRT_DEFAULT_COMPACT_PAGE_SIZE, MRT_PAGE_SIZE_OPTIONS } from '@shared/constants/mrt-page-size-options';
import { environment } from '@environments/environment';
import { ServerSideQueryParams } from '@rs-shared-lib/abstract-classes/rs-overview-state/models/rs-overview-server-side-state-manager';
import { TranslationService, RsUtils } from '@lib/rs-ngx';

export type DoubleMaintenanceFilters = {
  activityCodeFrom: string;
  activityCodeTo: string;
  activityDescription: string;
  vin: string;
  leasingContractGroupId: number | null;
  febiacBrandCode: string;
  febiacModel: string;
  firstRegistrationStartingFrom: string;
  febiacFuelCode: string;
  febiacPower: number | null;
  febiacCylinderContent: number | null;
  febiacTransmission: string;
};

@RsPersistOverviewState({
  storageKeySuffix: 'MrtLeasingCompanyDoubleMaintenance',
  includeUserContext: () => inject(UserService).getCurrentUser()!.loginString,
})
@Injectable()
export class DoubleMaintenanceOverviewStateService extends RsOverviewServerSideStateManager<DoubleMaintenanceSummary, DoubleMaintenanceFilters> {
  public readonly filterCount = computed(() => rsCountFilters(this.filters()));

  constructor() {
    const http = inject(HttpClient);
    const currentLanguage = inject(TranslationService).currentLang;

    super({
      serviceMethod: (params: ServerSideQueryParams<DoubleMaintenanceFilters>) => {
        const {
          _page, _pageSize, _sortBy, _sortDirection, ...restFilters
        } = params;

        return http.get<DoubleMaintenanceSummary[]>(
          `${environment.apiUrl}/leasing-company/double-maintenance`,
          {
            params: RsUtils.trimEmptyHttpParams({
              ...restFilters,
              page: _page,
              resultsPerPage: _pageSize,
              sortBy: _sortBy,
              sortDirection: _sortDirection,
              userLanguage: currentLanguage(),
            }),
            observe: 'response',
          },
        );
      },
      initialFiltersState: {
        activityCodeFrom: '',
        activityCodeTo: '',
        activityDescription: '',
        vin: '',
        leasingContractGroupId: null,
        febiacBrandCode: '',
        febiacModel: '',
        firstRegistrationStartingFrom: '',
        febiacFuelCode: '',
        febiacPower: null,
        febiacCylinderContent: null,
        febiacTransmission: '',
      },
      initialSortingState: { active: 'ACTIVITY_CODE', direction: 'asc' },
      initialPagination: { pageIndex: 0, pageSize: MRT_DEFAULT_COMPACT_PAGE_SIZE },
      config: { pageSizeOptions: MRT_PAGE_SIZE_OPTIONS },
    });
  }
}
